import {AxisScale, TickFormatter} from '@visx/axis';
import {Group} from '@visx/group';
import {scaleBand, ScaleInput, scaleLinear} from '@visx/scale';
import {LinePath} from '@visx/shape';
import {useTooltip, useTooltipInPortal} from '@visx/tooltip';
import {flatMap} from 'lodash';
import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';
import {animated, SpringValue, useSpring, useSprings} from 'react-spring';
import styled from 'styled-components';

import {randomString} from '../../../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {useMeasureElement} from '../../../../../../libs/shared/ui/src/hooks/domHookHelpers';
import {newPalette, NewPaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DataAccessorResult} from '../../commons/accessors';
import {ChartProps, ChartSerie, ChartSerieValueAccesor} from '../../commons/types';
import {useMaxInRange} from '../../helpers/ranges';
import {XAxis} from '../axis/xAxis';
import {YAxis, yAxisWidth} from '../axis/yAxis';
import {GridRows} from '../grid/rows';

/*
 * Const.
 */

const findLoadingLineColor = (theme: UserTheme) => theme.greys.shade30;
const lineStrokeWidth = 2;
const findTooltipLineColor = (theme: UserTheme) => theme.greys.shade40;
// How much spacing there should be between our tooltip
// and the gray vertical line
const tooltipLineOffset = 16;
const tooltipMinWidth = 120;

// 14px line height + 8px margin on top + 8px margin on bottom.
// Needed to animate the positions within the tooltip
const tooltipDataPointHeight = 30;

// Config values for our spring physics
const springAnimationConfig = {
  friction: 26,
  tension: 250,
  clamp: true,
};

/*
 * Props.
 */

export interface LineChartProps<T> extends ChartProps<T> {
  xAxisTickFormatter?: TickFormatter<ScaleInput<AxisScale>>;
  yAxisTickFormatter?: ValueFormatter;
  tooltipValueFormatter?: ValueFormatter;
  tooltipLabelFormatter?: TooltipLabelFormatter<T>;
  tooltipHeaderFormatter?: TooltipHeaderFormatter;

  /** The max number of ticks on the Y axis. */
  maxYTicks?: number;
  /** The max number of ticks on the X axis. */
  maxXTicks?: number;

  /** Array of series used for the legend and unique bar colors. */
  series: ReadonlyArray<ChartSerie<T>>;

  onClick?: (arg: LineChartEventData<T>) => void;
}

/*
 * Types.
 */

export type LineChartEventData<T> = {data: T; serie: ChartSerie<T>};

export type TooltipHeaderFormatter = (xValue: DataAccessorResult) => ReactNode;

type TooltipLabelFormatter<T> = (data: T, serieIndex: number) => ReactNode;

type ValueFormatter = (value: number) => string;

interface TooltipDataPoint {
  label: ReactNode;
  color: string;
  value: number;
  formattedValue: string;
  key: string;
  sortedIndex: number;
}

interface TooltipData {
  currentTooltipXIndex: number;
  tooltipLeftNoOffset: number;
  tooltipHeader: DataAccessorResult;
  tooltipDataPoints: TooltipDataPoint[];
}

interface TooltipPositioning {
  containerMaxY: number;
  offsetXPoints: number[];
  tooltipY: number;
  tooltipLineLeft: number | undefined;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
interface XCoordinate {
  value: number;
  index: number;
}

type TooltipPointHover =
  | {
      yPoint: number;
      yIndex: number;
    }
  | undefined;

/*
 * Styles.
 */

const StyledTooltipContainer = styled.div`
  min-width: ${tooltipMinWidth}px;
  background-color: ${(p) => p.theme.foreground};

  ${(p) => boxShadow(p.theme, `0px 2px 4px ${p.theme.alphas.black40}`)};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px;
`;

const StyledTooltipHeader = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  padding: 8px;
`;

const StyledTooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px;
  width: 100%;
`;

const StyledTooltipLabel = styled.div`
  font-size: ${fontSizes.verySmall};
  display: flex;
  flex-direction: row;
  align-items: center;
  /* This is the minimum gap we want between a label and a value */
  margin-right: 16px;
`;

const StlyedLabelColor = styled.div<{$color: string}>`
  border-radius: 50%;
  background-color: ${(p) => p.$color};
  width: 10px;
  height: 10px;
  margin-right: 5px;
  margin-left: 1px;
`;

const StyledTooltipValue = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

const StyledSvg = styled.svg`
  overflow: visible;
`;

/*
 * Component.
 */

export const LineChart = <T,>({
  width,
  height,
  margin = {left: 10, top: 30},
  series,
  xAccessor,
  data,
  isLoading,
  color = NewPaletteColorsEnum.RED,
  xAxisTickFormatter,
  yAxisTickFormatter,
  maxYTicks,
  maxXTicks,
  onClick,
  tooltipHeaderFormatter = (xValue) => xValue.toString(),
  tooltipLabelFormatter,
  tooltipValueFormatter = yAxisTickFormatter,
}: LineChartProps<T>): ReturnType<FC> => {
  const theme = useUserTheme();
  /** Style calculations. */
  const groupTopOffset = margin.top || 0;
  const groupLeftOffset = yAxisWidth + (margin.left || 0);
  const gridRowsWidth = width - (margin.left || 0) - (margin.right || 0) - yAxisWidth;

  const {tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, updateTooltip} =
    useTooltip<TooltipData>();

  const {containerRef, containerBounds, TooltipInPortal} = useTooltipInPortal({
    scroll: true,
  });

  const [tooltipPointHover, setTooltipPointHover] = useState<TooltipPointHover>();

  const [tooltipRef, {width: tooltipWidth}] = useMeasureElement();

  const [xMax, yMax] = useMaxInRange(width, height, margin, true, true);

  const yAccessors: ReadonlyArray<ChartSerieValueAccesor<T>> = useMemo(
    () => series.map((serie) => (d: T) => serie.accessor(d)),
    [series],
  );

  const yValues = flatMap(yAccessors, (yAcc) => data.map(yAcc));
  const maxYValue = Math.max(...yValues);

  const [xScale, yScale] = useMemo(
    () => [
      scaleBand({
        range: [0, xMax],
        domain: data.map((d) => xAccessor(d)),
        paddingOuter: 0.2,
        paddingInner: 1,
      }),
      scaleLinear({
        range: [yMax, 0],
        domain: [0, maxYValue || 1],
        round: true,
      }),
    ],
    [xMax, data, yMax, maxYValue, xAccessor],
  );

  const [computeXPoint, computeYPoints] = useMemo(
    () => [(d: T) => xScale(xAccessor(d)), yAccessors.map((yAcc) => (d: T) => yScale(yAcc(d)))],
    [xScale, yScale, xAccessor, yAccessors],
  );

  const {containerMaxY, offsetXPoints, tooltipY, tooltipLineLeft} = computeTooltipPositioning(
    yMax,
    groupTopOffset,
    groupLeftOffset,
    tooltipData,
    data,
    computeXPoint,
  );

  /**
   * Given an x position on the chart, calculates the position and index in the data of the
   * closest x coordinate
   */
  const calculateClosestXCoordinate = useCallback(
    (mouseX: number) =>
      offsetXPoints.reduce((prev: XCoordinate | undefined, curr, currIndex): XCoordinate => {
        // This reduce is written like this to avoid trying to access the data at array index 0 as
        // the default - instead on the first iteration we grab that data. If for some reason
        // there are no valid X points, the result will just be undefined.
        if (!prev) {
          return {value: curr, index: currIndex};
        }
        return Math.abs(curr - mouseX) < Math.abs(prev.value - mouseX)
          ? {value: curr, index: currIndex}
          : prev;
      }, undefined as XCoordinate | undefined),
    [offsetXPoints],
  );

  const clearDataAndHideTooltip = useCallback(() => {
    updateTooltip({
      tooltipLeft: undefined,
      tooltipTop: undefined,
      tooltipOpen: false,
      tooltipData: undefined,
    });
  }, [updateTooltip]);

  /**
   * The main tooltip mouse move event handler - takes the current pointer position and calculates
   * if the tooltip position needs to update to snap to a new line, or hide (if the cursor leaves
   * the area of the chart).
   */
  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      // Coordinates are relative to the container in which tooltip is rendered.
      const mouseX = event.clientX - containerBounds.left;
      const mouseY = event.clientY - containerBounds.top;

      // Don't render the tooltip if we are over either axis.
      if (mouseX < groupLeftOffset || mouseY > containerMaxY) {
        if (tooltipOpen) {
          clearDataAndHideTooltip();
        }
        return;
      }

      // Tooltip should snap to the closest x-coordinate.
      const closestX = calculateClosestXCoordinate(mouseX);

      // This will only happen if there are no xPoints, in which case we don't
      // really have a reason to show the tooltip :p
      if (!closestX) {
        return;
      }

      const {value: tooltipX, index: xIndex} = closestX;

      // would the tooltip extend past the right edge of the chart if we rendered it on the
      // right side of the line
      const doesTooltipExceedChartBounds = tooltipX + tooltipWidth > width;

      // The tooltip should flip to the left side of the line if it would otherwise
      // render outside of the bounds of our chart.
      const tooltipLeftOffset = doesTooltipExceedChartBounds
        ? computeTooltipXLeftOfLine(tooltipX, tooltipWidth)
        : tooltipX;

      const tooltipDataPoints = computeTooltipDataPoints(
        data,
        series,
        xIndex,
        tooltipValueFormatter,
        tooltipLabelFormatter,
      );

      // The tooltip label is just the x-axis value the corresponds to the current tooltip location
      const tooltipHeader = xAccessor(data[xIndex]);

      showTooltip({
        tooltipLeft: tooltipLeftOffset,
        tooltipTop: tooltipY,
        tooltipData: {
          currentTooltipXIndex: xIndex,
          tooltipLeftNoOffset: tooltipX,
          tooltipHeader,
          tooltipDataPoints,
        },
      });
    },
    [
      containerBounds.left,
      containerBounds.top,
      groupLeftOffset,
      containerMaxY,
      calculateClosestXCoordinate,
      tooltipWidth,
      width,
      series,
      yAxisTickFormatter,
      xAccessor,
      data,
      showTooltip,
      tooltipY,
      tooltipOpen,
      clearDataAndHideTooltip,
    ],
  );

  const numYTicks = useMemo(() => {
    if (maxYValue === 0) {
      return 0.5; // We only want to display the first "0" tick. For some reason returning 1 here displays 2 ticks. But this works.
    }

    if (!maxYTicks) {
      return undefined;
    }

    const uniqueValues = new Set(yValues);

    return Math.min(maxYTicks, uniqueValues.size);
  }, [maxYValue, maxYTicks, yValues]);

  const renderLine = useCallback(
    (d: ReadonlyArray<T>) =>
      computeYPoints.map((computeYPoint, i) => {
        // If the color from the series is undefined fallback to the color supplied in the defaults.
        const strokeColor = series[i].color || newPalette[color].regular;
        const stroke = isLoading ? findLoadingLineColor(theme) : strokeColor;

        return (
          <LinePath
            // eslint-disable-next-line react/no-array-index-key
            key={`line-${i}`}
            data={[...d]}
            x={(p) => computeXPoint(p) ?? 0}
            y={(p) => computeYPoint(p) ?? 0}
            stroke={stroke}
            strokeWidth={lineStrokeWidth}
            shapeRendering="geometricPrecision"
          />
        );
      }),
    [computeYPoints, isLoading, series, color, computeXPoint],
  );

  // The vertical positions of our highlight data points - this is an ordered array with the position
  // of each data point in the same position as that line in the data
  const tooltipHighlightYPoints = computeYPoints.map((computeYPoint) => {
    if (tooltipData === undefined || tooltipData.currentTooltipXIndex === undefined) {
      return undefined;
    }
    return computeYPoint(data[tooltipData.currentTooltipXIndex]);
  });

  // Config values for our spring physics

  // animating the tooltop and the tooltip vertical line
  const {tooltipLineLeftAnimated, tooltipLeftAnimated} = useSpring({
    tooltipLineLeftAnimated: tooltipLineLeft,
    tooltipLeftAnimated: tooltipLeft,
    config: springAnimationConfig,
  });

  const tooltipValues = tooltipData?.tooltipDataPoints ?? [];

  // animating the elemnts in the tooltip array
  const tooltipValuesSprings = useSprings(
    tooltipValues.length,
    tooltipValues.map((tooltipDataPoint, index) => ({
      // This is the amount we should offset the line from it's normal position in the list.
      // So for example if an item is currently at index 2, but we want to render it at index
      // 0, this will return an offset of (0 - 2) * 30 = -60 to transform the top
      // of this line by.
      dataPointTopOffset: (tooltipDataPoint.sortedIndex - index) * tooltipDataPointHeight,
      config: springAnimationConfig,
    })),
  );

  // animating the highlight points
  const highlightSprings = useSprings(
    tooltipHighlightYPoints.length,
    tooltipHighlightYPoints.map((highlighYPoint) => ({
      highlighYPoint,
      config: springAnimationConfig,
    })),
  );

  const AnimatedTooltip = animated(TooltipInPortal);

  const isTooltipVisible = !isLoading && tooltipOpen;

  return (
    <StyledSvg
      width={width}
      height={height}
      ref={containerRef}
      onPointerMove={handlePointerMove}
      onMouseLeave={clearDataAndHideTooltip}
    >
      <Group top={groupTopOffset} left={groupLeftOffset}>
        <GridRows width={gridRowsWidth} scale={yScale} numTicks={numYTicks} />
        {/* svg z-index is determined by rendering order, so we render the x and y axis before the data - so that
        if we have zeros in the data the lines do not get clipped off by the axis but rather render on top */}
        <XAxis
          top={yMax}
          scale={xScale}
          hideTickValues={isLoading}
          tickFormat={xAxisTickFormatter}
          numTicks={maxXTicks}
        />
        <YAxis
          scale={yScale}
          hideTickValues={isLoading}
          tickFormat={yAxisTickFormatter}
          numTicks={numYTicks}
        />
        <Group>{renderLine(data)}</Group>
        {isTooltipVisible && (
          <g>
            <animated.line
              x1={tooltipLineLeftAnimated}
              y1={0}
              x2={tooltipLineLeftAnimated}
              y2={yMax}
              stroke={findTooltipLineColor(theme)}
              strokeWidth={lineStrokeWidth}
              pointerEvents="none"
            />
            {renderTooltipLineHighlights(
              tooltipLineLeftAnimated,
              highlightSprings,
              tooltipHighlightYPoints,
              series,
              data,
              tooltipData,
              color,
              tooltipPointHover,
              setTooltipPointHover,
              onClick,
            )}
            {tooltipPointHover && (
              <animated.circle
                cx={tooltipLineLeft}
                cy={tooltipPointHover.yPoint}
                r={8}
                fillOpacity="0"
                stroke={newPalette[NewPaletteColorsEnum.BLUE].highlight}
                strokeWidth={6}
                pointerEvents="none"
              />
            )}
          </g>
        )}
        {isTooltipVisible && (
          <AnimatedTooltip
            top={tooltipTop}
            left={tooltipLeftAnimated}
            offsetLeft={tooltipLineOffset}
            unstyled
            applyPositionStyle
          >
            {renderTooltip(
              tooltipData,
              tooltipRef,
              tooltipValuesSprings,
              tooltipPointHover,
              tooltipHeaderFormatter,
            )}
          </AnimatedTooltip>
        )}
      </Group>
    </StyledSvg>
  );
};

/*
 * Helpers.
 */

/**
 * Handles calculations for the positioning of the tooltip correctly in the chart
 *
 * @param yMax the max yValue
 * @param groupTopOffset the offset for the top of the group containing the chart
 * @param groupLeftOffset the offset for the left of the group containing the chart
 * @param gridRowsWidth the width of the grid rows in the chart
 * @param tooltipData the current TooltipData
 * @param data the chart data
 * @param computeXPoint function for computing the xPoint position from the chart data
 * @returns TooltipPositioning
 */
function computeTooltipPositioning<T>(
  yMax: number,
  groupTopOffset: number,
  groupLeftOffset: number,
  tooltipData: TooltipData | undefined,
  data: ReadonlyArray<T>,
  computeXPoint: (d: T) => number | undefined,
): TooltipPositioning {
  // The lower axis line of the chart is drawn at the yMax + the top offset.
  const containerMaxY = yMax + groupTopOffset;
  // The tooltip is always rendered at the same height - 10% off of the top
  // of the container.
  const tooltipY = containerMaxY * 0.1;
  // These are the actual positions on the x axis of all our values relative to the chart
  // group - needed for calculating the positions that we snap the line too.
  const xPoints = data.map((d) => computeXPoint(d)).filter((point): point is number => point !== undefined);
  // The position of our xPoints after accounting for the left offset in the chart.
  // The left offset is the space used for the y axis of this chart.
  // Needed for computing the position of the tooltip since it renders in a portal
  // that isn't just relative to the chart group but the whole SVG
  const offsetXPoints = xPoints.map((point) => point + groupLeftOffset);
  // Because the tooltip line is rendered within the main chart area rather than in a
  // seperate portal like the tooltip itself we need to subtract the offset for the left
  // group from it's position.
  const tooltipLineLeft = tooltipData?.tooltipLeftNoOffset
    ? tooltipData.tooltipLeftNoOffset - groupLeftOffset
    : undefined;

  return {containerMaxY, offsetXPoints, tooltipY, tooltipLineLeft};
}

/**
 * Renders the data points that are at the intersection of each of the lines
 * and the current tooltip vertical line
 */
function renderTooltipLineHighlights<T>(
  tooltipLineLeftAnimated: SpringValue<number> | undefined,
  tooltipHighlightPointsAnimated: {highlighYPoint: SpringValue<number> | undefined}[],
  tooltipHighlightYPoints: ReadonlyArray<number | undefined>,
  series: ReadonlyArray<ChartSerie<T>>,
  data: ReadonlyArray<T>,
  tooltipData: TooltipData | undefined,
  color: NewPaletteColorsEnum,
  tooltipPointHover: TooltipPointHover,
  setTooltipPointHover: (hover: TooltipPointHover) => void,
  onClick?: (arg: LineChartEventData<T>) => void,
) {
  const tooltipHighlights = tooltipHighlightYPoints.map((yPoint, i) => {
    const currentHighlight = tooltipHighlightPointsAnimated[i];
    const currentXindex = tooltipData?.currentTooltipXIndex;
    if (!currentHighlight || yPoint === undefined || currentXindex === undefined) {
      return null;
    }

    // If the color from the series is undefined fallback to the color supplied in the defaults.
    const strokeColor = series[i].color ?? newPalette[color].regular;

    const key = `line-highlight-point-${i}`;

    // We only want to track the hover state when this highlight point is not currently in an animated state
    const isAnimating = tooltipLineLeftAnimated?.isAnimating || currentHighlight.highlighYPoint?.isAnimating;

    const onMouseMove = () => {
      if (!tooltipPointHover && !isAnimating) {
        setTooltipPointHover({yPoint, yIndex: i});
      }
    };

    return (
      <animated.circle
        key={key}
        cx={tooltipLineLeftAnimated}
        cy={currentHighlight.highlighYPoint}
        r={4}
        fill={strokeColor}
        stroke="white"
        strokeWidth={2}
        onMouseMove={onMouseMove}
        onFocus={onMouseMove}
        onMouseOut={() => setTooltipPointHover(undefined)}
        onBlur={() => setTooltipPointHover(undefined)}
        onClick={() => {
          if (onClick) {
            onClick({data: data[currentXindex], serie: series[i]});
          }
        }}
      />
    );
  });

  // The z-indexs in svgs are determined by rendering order, so if there is a currently hovered point
  // we want to move it to the end of the array before rendering the points to make sure it isn't covered
  // by any overlapping highlight points.
  if (tooltipPointHover) {
    // Remove the hovered point from the array.
    const hoveredPoint = tooltipHighlights.splice(tooltipPointHover.yIndex, 1)[0];
    // Add the point back to the end of the array to make sure that it renders last.
    tooltipHighlights.push(hoveredPoint);
  }

  return tooltipHighlights;
}

/**
 * Computes the data points that we display in the tooltip based on the current xIndex
 */
function computeTooltipDataPoints<T>(
  data: ReadonlyArray<T>,
  series: ReadonlyArray<ChartSerie<T>>,
  xIndex: number,
  tooltipValueFormatter?: ValueFormatter,
  tooltipLabelFormatter?: TooltipLabelFormatter<T>,
): TooltipDataPoint[] {
  const tooltipDataPointsBase = series.map((serie, serieIndex) => {
    const value = serie.accessor(data[xIndex]);
    const formattedValue = tooltipValueFormatter?.(value) ?? value.toString();
    const formattedLabel = tooltipLabelFormatter?.(data[xIndex], serieIndex) ?? serie.label;

    return {
      label: formattedLabel,
      color: serie.color,
      value,
      formattedValue,
      // We use this random key to figure out the order of this element in the sorted array
      key: randomString(),
    };
  });

  // We want to get teh sorted order without actually sorting the original array (since we
  // want to maintain the order to render the animations correctly) - so we make a copy of the
  // array first and then sort it
  const sorted = tooltipDataPointsBase.slice(0).sort((a, b) => b.value - a.value);

  // We then use that sorted array and the `key` property to find the position of
  // each element in the sorted array, and attach it to the object
  const tooltipDataPoints = tooltipDataPointsBase.map((dataPoint) => {
    const sortedIndex = sorted.findIndex((point) => point.key === dataPoint.key);
    return {
      ...dataPoint,
      sortedIndex,
    };
  });

  return tooltipDataPoints;
}

function renderTooltip(
  tooltipData: TooltipData | undefined,
  tooltipRef: (arg: HTMLDivElement | null) => void,
  tooltipDataPointsAnimated: {dataPointTopOffset: SpringValue<number> | undefined}[],
  tooltipPointHover: TooltipPointHover,
  tooltipHeaderFormatter: TooltipHeaderFormatter,
) {
  if (!tooltipData) {
    return null;
  }

  return (
    <StyledTooltipContainer ref={tooltipRef}>
      <StyledTooltipHeader>{tooltipHeaderFormatter(tooltipData.tooltipHeader)}</StyledTooltipHeader>
      {tooltipData.tooltipDataPoints.map((dataPoint, index) =>
        renderTooltipLine(
          dataPoint,
          tooltipDataPointsAnimated[index].dataPointTopOffset,
          tooltipPointHover,
          index,
        ),
      )}
    </StyledTooltipContainer>
  );
}

function renderTooltipLine(
  dataPoint: TooltipDataPoint,
  dataPointTop: SpringValue<number> | undefined,
  tooltipPointHover: TooltipPointHover,
  index: number,
) {
  const AnimatedTooltipRow = animated(StyledTooltipRow);

  // Move the tooltip row by the amount specified in the dataPoint.
  const transform = dataPointTop?.to((y) => `translateY(${y}px)`);

  // If there is a hover active, set the opacity of every non-hovered line to 50%.
  const isActiveHoverAndNotHovered = tooltipPointHover !== undefined && index !== tooltipPointHover.yIndex;
  const opacity = isActiveHoverAndNotHovered ? '50%' : '100%';

  return (
    <AnimatedTooltipRow key={dataPoint.key} style={{transform, opacity}}>
      <StyledTooltipLabel>
        <StlyedLabelColor $color={dataPoint.color} />
        {dataPoint.label}
      </StyledTooltipLabel>
      <StyledTooltipValue>{dataPoint.formattedValue}</StyledTooltipValue>
    </AnimatedTooltipRow>
  );
}

/**
 * Compute the position of the tooltip if we are rendering it on the left side of the
 * line. Needed when the tooltip would otherwise render outside of the chart
 */
function computeTooltipXLeftOfLine(tooltipX: number, tooltipWidth: number): number {
  // Need to calculate for the offset on both sides of the line to move the tooltip
  // over to the correct position if we are flipping it to the other side of the line.
  const totalLineOffset = tooltipLineOffset * 2;

  // The X value for the tooltip if we want to render it to the left of the tooltip line
  return tooltipX - tooltipWidth - totalLineOffset;
}
